import React, { useState, useEffect } from 'react';
import { loadClients, loadUsers } from '../utils/storage';

const MetaBudgetReviewForm = ({ record, onSave, onClose, currentUser }) => {
  const [formData, setFormData] = useState({
    mes: '',
    agente_seguimiento: '',
    cliente: '',
    cuenta_publicitaria: '',
    celula: '',
    estado: 'pendiente',
    nombre_campana: '',
    fecha_pago: '',
    monto_pago: '',
    asunto: '',
    acciones_tomadas: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [users, setUsers] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Cargar clientes y usuarios al montar el componente
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar clientes
        setLoadingClients(true);
        const clientsData = await loadClients();
        setClients(clientsData || []);
        
        // Cargar usuarios/agentes
        setLoadingUsers(true);
        const usersData = await loadUsers();
        setUsers(usersData || []);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        setClients([]);
        setUsers([]);
      } finally {
        setLoadingClients(false);
        setLoadingUsers(false);
      }
    };
    
    loadData();
  }, []);

  useEffect(() => {
    if (record) {
      // Formatear la fecha para el input date
      const fechaFormateada = record.fecha_pago 
        ? new Date(record.fecha_pago).toISOString().split('T')[0]
        : '';
      
      setFormData({
        mes: record.mes || '',
        agente_seguimiento: record.agente_seguimiento || '',
        cliente: record.cliente || '',
        cuenta_publicitaria: record.cuenta_publicitaria || '',
        celula: record.celula || '',
        estado: record.estado || 'pendiente',
        nombre_campana: record.nombre_campana || '',
        fecha_pago: fechaFormateada,
        monto_pago: record.monto_pago || '',
        asunto: record.asunto || '',
        acciones_tomadas: record.acciones_tomadas || ''
      });
      
      // Establecer el término de búsqueda del cliente
      setClientSearchTerm(record.cliente || '');
    } else {
      // Para nuevos registros, establecer valores por defecto
      const currentDate = new Date().toISOString().split('T')[0];
      const currentMonth = new Date().toLocaleDateString('es-ES', { 
        month: 'long'
      });
      
      setFormData(prev => ({
        ...prev,
        fecha_pago: currentDate,
        mes: currentMonth.charAt(0).toUpperCase() + currentMonth.slice(1),
        agente_seguimiento: currentUser?.firstName || currentUser?.name || currentUser?.email || ''
      }));
    }
  }, [record, currentUser]);

  // Filtrar clientes basado en el término de búsqueda
  useEffect(() => {
    if (clientSearchTerm.trim()) {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(clientSearchTerm.toLowerCase())
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients([]);
    }
  }, [clientSearchTerm, clients]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.mes.trim()) {
      newErrors.mes = 'El mes es requerido';
    }

    if (!formData.agente_seguimiento.trim()) {
      newErrors.agente_seguimiento = 'El agente de seguimiento es requerido';
    }

    if (!formData.cliente.trim()) {
      newErrors.cliente = 'El cliente es requerido';
    }

    if (!formData.cuenta_publicitaria.trim()) {
      newErrors.cuenta_publicitaria = 'La cuenta publicitaria es requerida';
    }

    if (!formData.celula.trim()) {
      newErrors.celula = 'La célula es requerida';
    }

    if (!formData.nombre_campana.trim()) {
      newErrors.nombre_campana = 'El nombre de la campaña es requerido';
    }

    if (!formData.fecha_pago) {
      newErrors.fecha_pago = 'La fecha de pago es requerida';
    }

    if (!formData.monto_pago || parseFloat(formData.monto_pago) <= 0) {
      newErrors.monto_pago = 'El monto de pago debe ser mayor a 0';
    }

    if (!formData.asunto.trim()) {
      newErrors.asunto = 'El asunto es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const mesesOptions = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const celulasOptions = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4',
    'G7M1', 'G7M2', 'G7M3', 'G7M4'
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente', color: 'text-yellow-600' },
    { value: 'en_proceso', label: '🔄 En Proceso', color: 'text-blue-600' },
    { value: 'completado', label: '✅ Completado', color: 'text-green-600' },
    { value: 'cancelado', label: '❌ Cancelado', color: 'text-red-600' }
  ];

  // Función para generar un nombre amigable desde firstName y lastName
  const getDisplayName = (user) => {
    // Prioridad 1: firstName + lastName
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    
    // Prioridad 2: Solo firstName
    if (user.firstName && user.firstName.trim()) {
      return user.firstName;
    }
    
    // Prioridad 3: Solo lastName
    if (user.lastName && user.lastName.trim()) {
      return user.lastName;
    }
    
    // Prioridad 4: Campo name (por compatibilidad)
    if (user.name && user.name.trim()) {
      return user.name;
    }
    
    // Prioridad 5: Generar desde email
    const emailPart = user.email.split('@')[0];
    
    // Mapeo específico para emails conocidos
    const emailMappings = {
      'admin': 'Admin User',
      'gcarvajalvidal': 'Gonzalo Carvajal',
      'carvajalg63': 'Gonzo Gonzalez',
      's': 'test test'
    };
    
    if (emailMappings[emailPart]) {
      return emailMappings[emailPart];
    }
    
    // Para otros emails, capitalizar y limpiar
    return emailPart
      .replace(/[._-]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Manejar cambio en el campo de cliente con autocompletado
  const handleClientChange = (e) => {
    const value = e.target.value;
    setClientSearchTerm(value);
    setFormData(prev => ({
      ...prev,
      cliente: value
    }));
    
    // Mostrar dropdown solo si hay texto y clientes filtrados
    if (value.trim()) {
      const filtered = clients.filter(client =>
        client.name.toLowerCase().includes(value.toLowerCase()) ||
        (client.specialty && client.specialty.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredClients(filtered.slice(0, 10)); // Limitar a 10 resultados
      setShowClientDropdown(filtered.length > 0);
    } else {
      setFilteredClients([]);
      setShowClientDropdown(false);
    }
    
    // Limpiar error del campo
    if (errors.cliente) {
      setErrors(prev => ({
        ...prev,
        cliente: ''
      }));
    }
  };

  // Seleccionar cliente del dropdown
  const handleClientSelect = (client) => {
    setFormData(prev => ({
      ...prev,
      cliente: client.name
    }));
    setClientSearchTerm(client.name);
    setShowClientDropdown(false);
    
    // Limpiar error si existe
    if (errors.cliente) {
      setErrors(prev => ({
        ...prev,
        cliente: ''
      }));
    }
  };

  // Calcular valores automáticos para mostrar preview
  const calculatePreview = () => {
    if (!formData.fecha_pago || !formData.monto_pago) return null;

    const fechaPago = new Date(formData.fecha_pago);
    const fechaVencimiento = new Date(fechaPago);
    fechaVencimiento.setDate(fechaVencimiento.getDate() + 30);

    const today = new Date();
    const diasRestantes = Math.max(0, Math.ceil((fechaVencimiento - today) / (1000 * 60 * 60 * 24)));
    const presupuestoDiario = diasRestantes > 0 ? parseFloat(formData.monto_pago) / diasRestantes : 0;
    
    const diasTranscurridos = Math.max(0, Math.ceil((today - fechaPago) / (1000 * 60 * 60 * 24)));
    const gastoAcumulado = presupuestoDiario * diasTranscurridos;
    const saldoDisponible = Math.max(0, parseFloat(formData.monto_pago) - gastoAcumulado);
    const porcentajeRestante = parseFloat(formData.monto_pago) > 0 ? (saldoDisponible / parseFloat(formData.monto_pago)) * 100 : 0;

    return {
      fechaVencimiento,
      diasRestantes,
      presupuestoDiario,
      saldoDisponible,
      porcentajeRestante
    };
  };

  const preview = calculatePreview();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">
                {record ? '✏️ Editar Registro' : '➕ Nuevo Registro'}
              </h2>
              <p className="text-blue-100 mt-1">
                {record ? 'Modificar información de revisión de presupuesto META' : 'Agregar nueva revisión de presupuesto META'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Fila 1: Mes, Agente de Seguimiento, Cliente */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mes *
                </label>
                <select
                  name="mes"
                  value={formData.mes}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.mes ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar mes...</option>
                  {mesesOptions.map(mes => (
                    <option key={mes} value={mes}>{mes}</option>
                  ))}
                </select>
                {errors.mes && <p className="text-red-500 text-sm mt-1">{errors.mes}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Agente de Seguimiento *
                </label>
                {loadingUsers ? (
                  <div className="w-full p-3 border-2 border-gray-300 rounded-xl flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-gray-500">Cargando agentes...</span>
                  </div>
                ) : (
                  <select
                    name="agente_seguimiento"
                    value={formData.agente_seguimiento}
                    onChange={handleChange}
                    className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                      errors.agente_seguimiento ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  >
                    <option value="">Seleccionar agente</option>
                    {users.map(user => (
                      <option key={user.email} value={getDisplayName(user)}>
                        {getDisplayName(user)} ({user.role || 'standard'})
                      </option>
                    ))}
                  </select>
                )}
                {errors.agente_seguimiento && <p className="text-red-500 text-sm mt-1">{errors.agente_seguimiento}</p>}
                {!loadingUsers && users.length === 0 && (
                  <p className="text-amber-600 text-sm mt-1">
                    No hay agentes disponibles. Asegúrate de tener usuarios registrados en el sistema.
                  </p>
                )}
              </div>

              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cliente *
                </label>
                <input
                  type="text"
                  name="cliente"
                  value={clientSearchTerm}
                  onChange={handleClientChange}
                  onFocus={() => setShowClientDropdown(clientSearchTerm.length > 0)}
                  onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                  placeholder="Buscar cliente existente..."
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.cliente ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                
                {/* Dropdown de clientes */}
                {showClientDropdown && filteredClients.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        onClick={() => handleClientSelect(client)}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">{client.name}</div>
                        {client.specialty && (
                          <div className="text-sm text-gray-500">{client.specialty}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {errors.cliente && <p className="text-red-500 text-sm mt-1">{errors.cliente}</p>}
              </div>
            </div>

            {/* Fila 2: Cuenta Publicitaria, Célula, Estado */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Cuenta Publicitaria *
                </label>
                <textarea
                  name="cuenta_publicitaria"
                  value={formData.cuenta_publicitaria}
                  onChange={handleChange}
                  placeholder="Información de la cuenta publicitaria"
                  rows="2"
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.cuenta_publicitaria ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.cuenta_publicitaria && <p className="text-red-500 text-sm mt-1">{errors.cuenta_publicitaria}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Célula *
                </label>
                <select
                  name="celula"
                  value={formData.celula}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.celula ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                >
                  <option value="">Seleccionar célula...</option>
                  {celulasOptions.map(celula => (
                    <option key={celula} value={celula}>{celula}</option>
                  ))}
                </select>
                {errors.celula && <p className="text-red-500 text-sm mt-1">{errors.celula}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Fila 3: Nombre de Campaña, Fecha de Pago, Monto */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre de la Campaña *
                </label>
                <textarea
                  name="nombre_campana"
                  value={formData.nombre_campana}
                  onChange={handleChange}
                  placeholder="Nombre de la campaña publicitaria"
                  rows="2"
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.nombre_campana ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.nombre_campana && <p className="text-red-500 text-sm mt-1">{errors.nombre_campana}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Pago *
                </label>
                <input
                  type="date"
                  name="fecha_pago"
                  value={formData.fecha_pago}
                  onChange={handleChange}
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.fecha_pago ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.fecha_pago && <p className="text-red-500 text-sm mt-1">{errors.fecha_pago}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monto que Pagó *
                </label>
                <input
                  type="number"
                  name="monto_pago"
                  value={formData.monto_pago}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                    errors.monto_pago ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                />
                {errors.monto_pago && <p className="text-red-500 text-sm mt-1">{errors.monto_pago}</p>}
              </div>
            </div>

            {/* Preview de cálculos automáticos */}
            {preview && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Cálculos Automáticos (Preview)</h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="text-center">
                    <span className="block text-blue-700 font-medium">Fecha de Vencimiento</span>
                    <span className="text-gray-900">{preview.fechaVencimiento.toLocaleDateString('es-MX')}</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-blue-700 font-medium">Días Restantes</span>
                    <span className="text-gray-900">{preview.diasRestantes} días</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-blue-700 font-medium">Presupuesto Diario</span>
                    <span className="text-green-600 font-semibold">{formatCurrency(preview.presupuestoDiario)}</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-blue-700 font-medium">Saldo Disponible</span>
                    <span className="text-blue-600 font-semibold">{formatCurrency(preview.saldoDisponible)}</span>
                  </div>
                  <div className="text-center">
                    <span className="block text-blue-700 font-medium">Porcentaje Restante</span>
                    <span className={`font-semibold ${
                      preview.porcentajeRestante >= 70 ? 'text-green-600' :
                      preview.porcentajeRestante >= 40 ? 'text-yellow-600' :
                      preview.porcentajeRestante >= 20 ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {preview.porcentajeRestante.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Fila 4: Asunto */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Asunto *
              </label>
              <textarea
                name="asunto"
                value={formData.asunto}
                onChange={handleChange}
                placeholder="Descripción detallada del asunto de la revisión de presupuesto..."
                rows="3"
                className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                  errors.asunto ? 'border-red-500' : 'border-gray-300'
                }`}
                required
              />
              {errors.asunto && <p className="text-red-500 text-sm mt-1">{errors.asunto}</p>}
            </div>

            {/* Fila 5: Acciones Tomadas */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Acciones Tomadas
              </label>
              <textarea
                name="acciones_tomadas"
                value={formData.acciones_tomadas}
                onChange={handleChange}
                placeholder="Describe las acciones tomadas para la revisión del presupuesto..."
                rows="3"
                className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Guardando...
              </div>
            ) : (
              record ? '💾 Actualizar' : '➕ Crear Registro'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaBudgetReviewForm;
